import {_SERVER_SORT_TYPE} from "@app/constants/searchTypes";
import dayjs from "dayjs";
import {
    _SERVER_PAYMENT_METHOD_TYPE,
    _SERVER_WEEK_TYPE,
    DATE_FORMAT_DASH,
    DATE_TIME_SEC_FORMAT,
    MONTH_FORMAT_DOT,
    PAYMENT_NAME_TYPE,
    SERVER_CAFE_ORDER_DEVICE_TYPE,
    SERVER_DINING_ORDER_DEVICE_TYPE,
    SERVER_GOODS_ORDER_TYPE,
    STATISTICS_DURATION_TYPE,
    STATISTICS_SALES_GRAPH_TYPE,
    STATISTICS_SEARCH_TYPE,
    TIME_MIN_FORMAT,
    WeekType
} from "@app/constants/types";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {GoodsPricePlanEntity} from "@services/entities/goodsEntity";
import {MealPricePlanEntity} from "@services/entities/mealEntity";
import {eAlert, eToast} from "@app/events";
import {Strings} from "@app/constants/strings";
import {MultiLangInputRefModel, PasswordCheckModel, StartTimeEndTimeOverlapModel} from "@contents/models/commonModel";
import {MultiLanguageEntity} from "@services/entities/commonEntity";
import 'dayjs/locale/ko';
import CONSTANTS from "@utils/constants";

dayjs.locale('ko');

/**
 * 0 채워넣기
 * @author Dongseek Son
 * @param {string|number} value - 값
 * @param {number} digit - 자릿수
 */
export const zeroPadding = (num: number, digit: number) => {
    let zero = '';
    for (let i = 0; i < digit; i++) {
        zero += '0';
    }
    return (zero + num).slice(-digit);
};

/**
 * 반올림
 * @author Dongseek Son
 * @param {string|number} num - 값
 * @param {number} digit - 자릿수 default=0
 */
export const roundOff = (num: any, digit: number = 0) => {
    return Number.parseFloat(num).toFixed(digit);
};

export const isEmptyString = (value: string) => {
    return value == undefined || value === '';
}

export const isEmptyObject = (param: any) => {
    return Object.keys(param).length === 0 && param.constructor === Object;
}
//
// export const arraySlice = (data: [], size = 1) => {
//   const arr = [];
//
//   for(let i = 0; i < data.length; i += size) {
//     arr.push(data.slice(i, i + size));
//   }
//
//   return arr;
// }

export const emailFormCheck = (email: string): boolean => {
    const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return emailRegex.test(email);
}

const getFieldData = (type: any, arrField: string[]) => {
    if (arrField == null || arrField.isEmptyArray()) {
        return '';
    }
    let temp = type;
    arrField.forEach((field) => {
        temp = temp[field]
    })

    return temp;

}

// 정렬하기 위해 값을 비교하는 함수
export const descendingComparator = <T>(a: T, b: T, order: string) => {
    const splitOrder = order ? order.split('.') : [];
    const compA = getFieldData(a, splitOrder);
    const compB = getFieldData(b, splitOrder);

    if (compB < compA) {
        return -1;
    }
    if (compB > compA) {
        return 1;
    }
    return 0;
}

// 값을 비교하고 sort()에 비교값을 반환
export function getComparator<Key extends keyof any>(
    orderBy: _SERVER_SORT_TYPE,
    order: string,
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string },
) => number {
    return orderBy === 'DESC'
        ? (a, b) => descendingComparator(a, b, order)
        : (a, b) => -descendingComparator(a, b, order);
}

// 비율 계산
export const rateCalculator = (total: number, score: number) => {
    return Math.floor((score / total) * 100);
}

export const rateRoundCalculator = (rate?: number, decimal = 2) => {
    return Math.round((rate || 0) * (10 * decimal)) / (10 * decimal);
}

export const styledOmit$PropsOptions = {
    shouldForwardProp: (propName: string) => !propName.startsWith('$')
};

// object deep copy
export function objectClone(source: object) {
    return JSON.parse(JSON.stringify(source));
}

export function isNew(date: string | undefined) {
    return dayjs(date).isSame(dayjs(), 'dates');
}

export function generateMonthList() {
    const months: Array<string> = [];
    let currentMonth = dayjs(); // 전월

    for (let i = 0; i < 24; i++) {
        months.push(currentMonth.format(MONTH_FORMAT_DOT)); // YYYY-MM 형식으로 저장
        currentMonth = currentMonth.subtract(1, 'month'); // 한 달씩 빼기
    }

    return months;
}

dayjs.extend(customParseFormat);

export function timeRangeString(startTime?: string, endTime?: string): string {
    return `${dayjs(startTime, TIME_MIN_FORMAT).format(TIME_MIN_FORMAT)} ~ ${dayjs(endTime, TIME_MIN_FORMAT).format(TIME_MIN_FORMAT)}`;
}

export function setSort(column: string, orderBy?: string) {
    return `${column},${orderBy ?? 'DESC'}`
}

export function orderGoodsPricePlans(a: GoodsPricePlanEntity, b: GoodsPricePlanEntity) {
    return (a.payMethodType === 'IDCARD' ? 1 : 0) - (b.payMethodType === 'IDCARD' ? 1 : 0);
}

export function getPriceTextByPaymentType(pricePlans?: Array<MealPricePlanEntity>, payMethodType?: _SERVER_PAYMENT_METHOD_TYPE, companyPriceYn?: boolean): string {
    if (pricePlans) {
        const normal = pricePlans?.find((plan) => plan?.payMethodType == 'NORMAL');
        if (payMethodType === 'NORMAL') {
            return `${(normal?.price || 0).toLocaleString()}원`;
        } else {
            const idCardPrice = pricePlans?.filter((plan) => plan?.payMethodType == 'IDCARD' && !plan?.companyId)?.[0];
            return (companyPriceYn) ? '법인별' : `${((normal?.price || 0) - (idCardPrice?.price || 0)).toLocaleString()}원`;
        }
    } else {
        return '';
    }
}

export function hasCompanyPrice(pricePlans?: Array<MealPricePlanEntity>): boolean {
    return pricePlans ? pricePlans?.filter((plan) => plan?.payMethodType == 'IDCARD' && plan?.companyId)?.length > 0 : false;
}

export function getStringByteSize(str: string): number {
    return new TextEncoder().encode(str).length;
}

export function getUploadSuccessToast() {
    eToast(Strings.alert.uploadSuccess);
}

export function getUploadErrorToast() {
    eToast(Strings.alert.uploadError);
}

export function getUploadErrorAlert(caption?: string) {
    eAlert(Strings.alert.uploadError, caption && caption);
}

export function getSaveSuccessToast() {
    eToast(Strings.alert.saveSuccess);
}

export function getDeleteSuccessToast() {
    eToast(Strings.alert.deleteSuccess);
}

export function getSaveErrorAlert(caption?: string) {
    eAlert(Strings.alert.saveError, caption && caption);
}

export function getDeleteErrorAlert(caption?: string) {
    eAlert(Strings.alert.deleteError, caption && caption);
}

export function getNoCheckErrorAlert(caption?: string) {
    eAlert(Strings.alert.noCheckError, caption && caption);
}

export function getExcelDownload(data) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'excel.xlsx');
    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(url);
}

export function checkValidateMultiLang(input?: MultiLangInputRefModel | null) {
    const data = input?.getValues() as MultiLanguageEntity;
    return data && data.ko !== '' && data.en !== '';
}

export const onErrorImg = (e) => {
    e.target.src = '/icon-no-image.svg'
}

export function checkStartTimeEndTime(intervals?: StartTimeEndTimeOverlapModel[]) {
    if (!intervals) {
        //데이터가 없을 경우
        return true;
    }

    for (let i = 0; i < intervals?.length; i++) {
        for (let j = i + 1; j < intervals?.length; j++) {
            const firstData = intervals?.[i];
            const secondData = intervals?.[j];

            if (firstData && secondData && firstData?.startTime && firstData?.endTime && secondData?.startTime && secondData?.endTime) {
                const start1 = dayjs(firstData?.startTime, TIME_MIN_FORMAT);
                const end1 = dayjs(firstData?.endTime, TIME_MIN_FORMAT);
                const start2 = dayjs(secondData?.startTime, TIME_MIN_FORMAT);
                const end2 = dayjs(secondData?.endTime, TIME_MIN_FORMAT);

                if (start1.isAfter(end1) || start2.isAfter(end2)) {
                    //종료시간이 시작시간보다 작을 경우
                    return true;
                }

                if (start1.isBefore(end2) && start2.isBefore(end1)) {
                    return true; // 겹치는 시간이 있을 경우
                }
            } else {
                //시간이 비어있을 경우
                return true;
            }
        }
    }
    return false; // 겹치는 시간이 없을 경우
}

// 숫자, -만 입력 가능
export const _validateNumber = (value: string, useMinus?: boolean) => {
    let val = value;
    if (useMinus) {
        val = val.replace(/[^0-9-]/g, '');

        const firstHyphen = val.indexOf('-');

        if (val === '0-') {
            val = '-'
        } else if (firstHyphen > 0) {
            val = val.replace(/-/g, '');
        } else if (firstHyphen === 0) {
            val = '-' + val.slice(1).replace(/-/g, '');
        }


        // if (val === '0-') val = '-';

    } else {
        val = val.replace(/[^0-9]/g, '');
    }
    return val;
    // return val === '' ? '0' : val;
};

// set 검색조건
export const setLocalSearchParams = <T>(searchParams: T) => {
    sessionStorage.setItem('searchParams', JSON.stringify(searchParams));
};

// get 검색조건
export const getLocalSearchParams = <T>() => {
    let prevParams = {};
    if (Object.keys(sessionStorage).includes('searchParams')) {
        const searchParams = JSON.parse(sessionStorage.getItem('searchParams')!);
        prevParams = {...searchParams};
    }
    return prevParams as T;
};

// remove 검색조건
export const removeLocalSearchParams = () => {
    sessionStorage.removeItem('searchParams');
};

/**
 * 썸네일 경로 생성
 * @author Dongseek Son
 * @param {string} path - 경로
 * @param {number | boolean} type - 썸네일 타입 (1~8)
 * @returns {string} - 썸네일 경로
 * @description type이 1~8(number)이면 썸네일 경로 반환, 아니면 원본 이미지 경로 반환,
 * thumbnailYn 활용시 type parameter에 thumbnailYn && type 으로 넣어서 활용 가능
 * @example getThumbPath(filePath, thumbnailYn && 3)
 */
export const getThumbPath = (path?: string, type?: number | boolean): string | undefined => {
    if (path && typeof type === 'number' && type >= 1 && type <= 8) {
        const filepathSeparatorRegex = /[\\/]/;
        const pathArray = path.split(filepathSeparatorRegex);
        const filepath = pathArray.slice(0, pathArray.length - 1).join('/');
        const filename = pathArray[pathArray.length - 1];
        const typeString = `_${zeroPadding(type, 2)}.webp`;
        return `${filepath}/thumb/${filename}${typeString}`;
    }
    return path; // type이 1~8(number)이 아니면 원본 이미지 반환
};

export const isEmptyArray = (data?: any) => {
    return !(data && data?.length > 0);
}

export const getUniqueValueInArray = (array?: any[]): any => {
    if (array) {
        return array?.filter((current, index, self) => self.indexOf(current) === index);
    } else {
        return [];
    }
}

export const getSumByGroupArray = (array: any, idKey: string, sumKey: string, sortKey?: string, max?: number, addedKeys?: string[]): any => {
    if (!array || array.length === 0) return [];

    const groupArray: any = Object.values(
        array.reduce((acc, item) => {
            if (!acc[item[idKey]]) {
                acc[item[idKey]] = sortKey ? {
                    [idKey]: item[idKey],
                    [sumKey]: 0,
                    [sortKey]: item[sortKey]
                } : {[idKey]: item[idKey], [sumKey]: 0}

                if (addedKeys && addedKeys?.length > 0) {
                    addedKeys?.forEach((key) => {
                        acc[item[idKey]][key] = item[key]
                    })
                }
            }
            acc[item[idKey]][sumKey] += item[sumKey];
            return acc;
        }, {})
    );

    if (sortKey && groupArray) {
        return groupArray.sort((a, b) => b[sortKey] - a[sortKey])?.slice(0, max || groupArray?.length);
    } else {
        return groupArray?.slice(0, max || groupArray?.length);
    }
}

export const getDayOfWeek = (index?: number): string => {
    if (index === undefined) {
        return `${dayjs().format('ddd')}`;
    } else {
        return `${dayjs().day(index).format('ddd')}`;
    }
}

export const getChartCaption = (selectedType: STATISTICS_SEARCH_TYPE, durationType?: STATISTICS_DURATION_TYPE, startDate?: string, endDate?: string, openAt?: string) => {
    let dateText = '';
    if (selectedType === 'DURATION') {
        durationType === 'DAY' ?
            (dateText = `${Strings.businessDate} ${startDate ? dayjs(startDate)?.format(DATE_FORMAT_DASH) : '-'}`)
            : (dateText = `${Strings.businessDate} ${startDate ? dayjs(startDate)?.format(DATE_FORMAT_DASH) : '-'} ~ ${endDate ? dayjs(endDate)?.format(DATE_FORMAT_DASH) : '-'}`);
    } else {
        dateText = `${Strings.businessStart} ${openAt ? dayjs(openAt)?.format(DATE_TIME_SEC_FORMAT) : '-'} ~`
    }

    return dateText;
}

export const getTimeRangeArray = (startTime: string, endTime: string, gap: number) => {
    let start = dayjs(startTime, TIME_MIN_FORMAT);
    let end = dayjs(endTime, TIME_MIN_FORMAT);
    if (end.isBefore(start) || end.isSame(start)) {
        end = end.add(1, 'day'); // end가 start보다 작으면 하루 더하기
    }
    const timeRange: string[] = [];

    while (start.isBefore(end)) {
        timeRange.push(start.format(TIME_MIN_FORMAT));
        start = start.add(gap, 'minute');
    }

    return timeRange;
}


export const groupBySameKey = (array: any, key: string): Map<string, any> => {
    const newMap = new Map<string, any>;
    array.forEach((data) => {
        if (newMap.has(data[key])) {
            //key가 있음
            const value = newMap.get(data[key]);
            value.push(data);
        } else {
            //key 없음
            newMap.set(data[key], [data]);
        }
    })

    return newMap;
}

export const getArrayDataByKey = <T>(array?: T[], keys?: Array<string>, idKey?: string, dataKey?: string) => {
    const resultArray: Array<number> = [];
    if (keys && keys?.length > 0 && idKey && dataKey) {
        keys?.forEach((key) => {
            const isExist = (array || []).find((data) => data?.[idKey] === key);
            if (isExist) {
                resultArray.push(isExist[dataKey]);
            } else {
                resultArray.push(0);
            }
        })
    }

    return resultArray;
}

export const getWeekdaysUnsorted = (startDate?: string, endDate?: string): _SERVER_WEEK_TYPE[] => {
    const start = dayjs(startDate || '');
    const end = dayjs(endDate || '');

    const dayDiff = end.diff(start, "day") + 1;
    const foundDays = new Set<_SERVER_WEEK_TYPE>();
    const week: _SERVER_WEEK_TYPE[] = WeekType;

    if (dayDiff >= 7) {
        // 7일 이상이면 무조건 전체 요일
        return week;
    }

    for (let i = 0; i < dayDiff; i++) {
        const currentDay = week[start.add(i, "day").day()] as _SERVER_WEEK_TYPE;
        foundDays.add(currentDay);
    }

    return Array.from(foundDays);
}

export const wonFormat = (amount?: number) => {
    if (amount === undefined || amount === 0) {
        return `₩ ${amount?.toLocaleString()}`;
    } else {
        return amount < 0 ? `- ₩ ${(amount * -1)?.toLocaleString()}` : `₩ ${(amount)?.toLocaleString()}`;
    }
}

export const pointFormat = (amount?: number) => {
    if (amount === undefined || amount === 0) {
        return `Ⓟ ${amount?.toLocaleString()}`;
    } else {
        return amount < 0 ? `- Ⓟ ${(amount * -1)?.toLocaleString()}` : `Ⓟ ${(amount)?.toLocaleString()}`;
    }
}

export const syncLists = <T extends { id?: number }>(prevList?: T[], newList?: T[]): T[] => {
    if (prevList && newList) {
        const result: T[] = [];

        const prevMap = new Map(prevList.filter(item => item?.id).map(item => [item?.id, item]));
        const newMap = new Map(newList.filter(item => item?.id).map(item => [item?.id, item]));

        for (const [id, bItem] of newMap.entries()) {
            if (!prevMap.has(id)) {
                result.push(bItem);
            } else {
                result.push(prevMap.get(id)!);
            }
        }

        const aItemsWithoutId = prevList.filter(item => !item.id);
        result.push(...aItemsWithoutId);

        return result;
    } else {
        return prevList || [];
    }
}

export const isArraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;

    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();

    return sorted1.every((val, index) => val === sorted2[index]);
}

export const contentMultiSpaceReplace = (content?: MultiLanguageEntity): MultiLanguageEntity => {
    return {
        ko: contentSpaceReplace(content?.ko),
        en: contentSpaceReplace(content?.en)
    }
}

export const contentSpaceReplace = (text?: string): string => {
    return (text || '').replace(/\t/g, '    ').replace(/ /g, '&nbsp;');
}

export const convertStatisticsItemEnumIntoText = (enumType?: string, type?: STATISTICS_SALES_GRAPH_TYPE): string => {
    let text: string = '';
    if(enumType){
        switch (type) {
            case 'CAFE_ORDER_DEVICE_TYPE':
                text = SERVER_CAFE_ORDER_DEVICE_TYPE?.[enumType];
                break;
            case 'DINING_ORDER_DEVICE_TYPE':
                text = SERVER_DINING_ORDER_DEVICE_TYPE?.[enumType];
                break;
            case 'PICKUP_TYPE':
                text = SERVER_GOODS_ORDER_TYPE?.[enumType];
                break;
            case 'PAID_METHOD':
                text = PAYMENT_NAME_TYPE?.[enumType];
                break;
            default:
                text = enumType || '';
        }
    }

    return text;
}

export const checkAppPassword = (password: string): string | null => {
    if(!CONSTANTS.RULES.APP_PASSWORD_RULE.test(password)){
        return Strings.desc.checkAppPasswordRule;
    }

    if(CONSTANTS.RULES.HAS_SAME_CHAR.test(password)){
        return Strings.desc.checkAppPasswordSameChar;
    }

    return null;
}

export const checkPassword = (password: string, type: 'OLD' | 'NEW' | 'CONFIRM' = 'NEW'): PasswordCheckModel | null => {
    if(password === '' || password == null) {
        //비밀번호를 입력해주세요
        return {
            type: 'NEW',
            message: Strings.desc.plzEnterPassword
        }
    }

    // if(!CONSTANTS.RULES.PASSWORD.test(password)){
    //     //비밀번호 형식을 확인해주세요 (영문, 숫자, 특수문자 중 2종류 이상 + 10~20 자리)
    //     return {
    //         type: 'NEW',
    //         message: Strings.desc.checkPasswordRule
    //     }
    // }

    if(!CONSTANTS.RULES.PASSWORD_CHAR.test(password)){
        //비밀번호 형식을 확인해주세요 (영문, 숫자, 특수문자(?!@&^~*#$+=) 중 2종류 이상)
        return {
            type: 'NEW',
            message: Strings.desc.checkPasswordRuleChar
        }
    }

    if(!CONSTANTS.RULES.PASSWORD_LENGTH.test(password)){
        //비밀번호 길이를 확인해주세요 (10~20 자리)
        return {
            type: 'NEW',
            message: Strings.desc.checkPasswordRuleLength
        }
    }
    return null;
}

export const checkNewPassword = (oldPassword: string, newPassword: string, newPasswordAgain?: string): PasswordCheckModel | null => {
    if(oldPassword === '' || oldPassword == null) {
        //비밀번호를 입력해주세요
        return {
            type: 'OLD',
            message: Strings.desc.plzEnterPassword
        }
    }

    const newPasswordCheck = checkPassword(newPassword, 'NEW');
    if(newPasswordCheck != null){
        return newPasswordCheck;
    }

    if(oldPassword.trim() === newPassword.trim()){
        //이전 비밀번호와 다른 비밀번호로 사용해주세요.
        return {
            type: 'NEW',
            message: Strings.desc.checkOldAndNewPassword
        }
    }

    if(newPassword !== newPasswordAgain){
        //비밀번호가 일치하지 않습니다. 비밀번호를 확인해주세요.
        return {
            type: 'CONFIRM',
            message: Strings.desc.checkConfirmPassword
        }
    }

    return null;
}